import React, { useContext, useEffect } from 'react';
import { Route } from 'react-router-dom';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
    return extendStatics(d, b);
};

function __extends(d, b) {
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) if (e.indexOf(p[i]) < 0)
            t[p[i]] = s[p[i]];
    return t;
}

/**
 * More specific page key priorities are used when multiple PageKeySetters are mounted
 */

var PageKeyPriority;

(function (PageKeyPriority) {
  /**
   * Page key definition against top level paths (single segment, e.g.: `/about-us`).
   */
  PageKeyPriority[PageKeyPriority["TOP_LEVEL"] = 0] = "TOP_LEVEL";
  /**
   * Page key definition against specific pages (e.g.: `/experiences/123/foo-bar`).
   */

  PageKeyPriority[PageKeyPriority["PAGE"] = 1] = "PAGE";
  /**
   * Page key definition against slide out / modal style components (e.g.: cart slide out appending `#cart` to the url)
   */

  PageKeyPriority[PageKeyPriority["OVERLAY"] = 2] = "OVERLAY";
})(PageKeyPriority || (PageKeyPriority = {}));

var PageKeyWithPriority =
/** @class */
function () {
  function PageKeyWithPriority(pageKey, priority) {
    this.pageKey = pageKey;
    this.priority = priority;
  }

  return PageKeyWithPriority;
}();

var PageKeyStorageContext = React.createContext([]);
var PageKeySetterContextValue = React.createContext(function () {
  return function () {
    return null;
  };
});

var PageKeyProvider =
/** @class */
function (_super) {
  __extends(PageKeyProvider, _super);

  function PageKeyProvider(props) {
    var _this = _super.call(this, props) || this;

    _this.state = {
      pageKeys: [],
      activePageKey: null
    };
    _this.ssrState = null;
    _this.addPageKey = _this.addPageKey.bind(_this);
    return _this;
  }

  PageKeyProvider.prototype.onNewPageKey = function (oldActivePk, newActivePk) {
    var onNewPageKey = this.props.onNewPageKey;

    if (!onNewPageKey) {
      return;
    }

    if (oldActivePk !== newActivePk) {
      return onNewPageKey(newActivePk);
    }
  };

  PageKeyProvider.prototype.addPageKey = function (pageKey, priority) {
    if (process.env.TARGET === 'web') {
      return this.addPageKeyWeb(pageKey, priority);
    } else {
      return this.addPageKeySsr(pageKey, priority);
    }
  };

  PageKeyProvider.prototype.warnOnDuplicatesOfPriority = function (pageKeys, newPriority) {
    var matchingPriPageKeys = pageKeys.filter(function (value) {
      return value.priority === newPriority;
    });

    if (matchingPriPageKeys.length > 1) {
      console.warn("Multiple page keys of priority " + newPriority + " present! " + "Please update the code so at most one page key at this priority is mounted. " + 'Currently mounted page keys: ' + pageKeys.map(function (pk) {
        return pk.pageKey + "(" + pk.priority + ")";
      }).join(', '));
    }
  };
  /**
   * Handle adding and cleaning up page key sets for web (client browser).
   */


  PageKeyProvider.prototype.addPageKeyWeb = function (pageKey, priority) {
    var _this = this;

    var pkWithPri = new PageKeyWithPriority(pageKey, priority);
    this.setState(function (_a) {
      var pageKeys = _a.pageKeys,
          activePageKey = _a.activePageKey;
      var newPageKeys = pageKeys.concat([pkWithPri]);
      var newActivePageKey = selectHighestPriorityPageKey(newPageKeys);

      _this.onNewPageKey(activePageKey, newActivePageKey);

      _this.warnOnDuplicatesOfPriority(newPageKeys, priority);

      return {
        pageKeys: newPageKeys,
        activePageKey: newActivePageKey
      };
    });
    return function () {
      _this.setState(function (_a) {
        var pageKeys = _a.pageKeys,
            activePageKey = _a.activePageKey;
        var foundIdx = pageKeys.indexOf(pkWithPri);

        if (foundIdx === -1) {
          console.error("Couldn't find pageKey " + pageKey + " for removal! This can only happen if the clean up is invoked multiple times.");
          return {
            pageKeys: pageKeys,
            activePageKey: activePageKey
          };
        } else {
          var trimmedPageKeys = pageKeys.filter(function (_, i) {
            return i !== foundIdx;
          });
          var newActivePageKey = selectHighestPriorityPageKey(trimmedPageKeys);

          _this.onNewPageKey(activePageKey, newActivePageKey);

          return {
            pageKeys: trimmedPageKeys,
            activePageKey: newActivePageKey
          };
        }
      });
    };
  };
  /**
   * Handle adding and cleaning up page key sets for SSR (node server).
   *
   * Since lifecycle methods aren't evaluated on the server, this can be much more
   * straight forward and just record the most recent and highest priority page key without
   * worrying about cleaning up from unmounts.
   */


  PageKeyProvider.prototype.addPageKeySsr = function (pageKey, priority) {
    if (this.ssrState === null || this.ssrState.priority <= priority) {
      this.onNewPageKey(this.ssrState ? this.ssrState.pageKey : null, pageKey);
      this.ssrState = new PageKeyWithPriority(pageKey, priority);
    }

    return function () {
      return null;
    };
  };

  PageKeyProvider.prototype.render = function () {
    return React.createElement(PageKeySetterContextValue.Provider, {
      value: this.addPageKey
    }, React.createElement(PageKeyStorageContext.Provider, {
      value: this.state.pageKeys
    }, this.props.children));
  };

  return PageKeyProvider;
}(React.Component);

function selectHighestPriorityPageKey(pageKeys) {
  var currentPageKey = null; // Get the highest priority that was added most recently

  for (var _i = 0, pageKeys_1 = pageKeys; _i < pageKeys_1.length; _i++) {
    var pk = pageKeys_1[_i];

    if (currentPageKey === null || pk.priority >= currentPageKey.priority) {
      currentPageKey = pk;
    }
  }

  return currentPageKey ? currentPageKey.pageKey : null;
}

function usePageKey() {
  return selectHighestPriorityPageKey(useContext(PageKeyStorageContext));
}

var PageKeyConsumer = function PageKeyConsumer(_a) {
  var children = _a.children;
  return React.createElement(PageKeyStorageContext.Consumer, null, function (pageKeys) {
    return children(selectHighestPriorityPageKey(pageKeys));
  });
};

function PageKeySetter(_a) {
  var value = _a.value,
      priority = _a.priority;
  var setter = useContext(PageKeySetterContextValue);

  if (process.env.TARGET === 'web') {
    useEffect(function () {
      return setter(value, priority);
    }, [value, priority]);
  } else {
    // useEffect isn't evaluated during SSR. Since we don't need to worry about unmounting,
    // just blast all of the values over. The internal implementation will dedup, and use the
    // last set highest priority one.
    setter(value, priority);
  }

  return null;
}

/**
 * Wrapped react router Route; sets the specified page key if the route matches.
 */

var PageKeyRoute =
/** @class */
function (_super) {
  __extends(PageKeyRoute, _super);

  function PageKeyRoute(props) {
    var _this = _super.call(this, props) || this;

    _this.WrappedComponent = _this.WrappedComponent.bind(_this);
    _this.wrappedRender = _this.wrappedRender.bind(_this);
    _this.wrappedChildren = _this.wrappedChildren.bind(_this);
    return _this;
  }

  PageKeyRoute.prototype.WrappedComponent = function (componentProps) {
    var _a = this.props,
        pageKey = _a.pageKey,
        priority = _a.priority,
        component = _a.component;
    return React.createElement(React.Fragment, null, React.createElement(PageKeySetter, {
      value: pageKey,
      priority: priority
    }), React.createElement(component, componentProps));
  };

  PageKeyRoute.prototype.wrappedRender = function (renderProps) {
    var _a = this.props,
        pageKey = _a.pageKey,
        priority = _a.priority,
        render = _a.render;
    return React.createElement(React.Fragment, null, React.createElement(PageKeySetter, {
      value: pageKey,
      priority: priority
    }), render(renderProps));
  };

  PageKeyRoute.prototype.wrappedChildren = function (props) {
    var _a = this.props,
        children = _a.children,
        pageKey = _a.pageKey,
        priority = _a.priority;

    if (children) {
      var childrenRes = // @ts-ignore
      typeof children === 'function' ? children(props) : children;

      if (childrenRes) {
        return React.createElement(React.Fragment, null, props.match ? React.createElement(PageKeySetter, {
          value: pageKey,
          priority: priority
        }) : null, childrenRes);
      }
    }

    return null;
  };

  PageKeyRoute.prototype.render = function () {
    var _a = this.props,
        component = _a.component,
        render = _a.render,
        children = _a.children,
        remainingProps = __rest(_a, ["component", "render", "children"]);

    var WrappedComponent = component ? this.WrappedComponent : undefined;
    var wrappedRender = render ? this.wrappedRender : undefined;
    var wrappedChildren = children ? this.wrappedChildren : undefined;
    return React.createElement(Route, __assign({}, remainingProps, {
      component: WrappedComponent,
      render: wrappedRender
    }), wrappedChildren);
  };

  return PageKeyRoute;
}(React.Component);

export { PageKeyProvider, PageKeySetter, usePageKey, PageKeyConsumer, PageKeyPriority, PageKeyRoute };
//# sourceMappingURL=index.esm.js.map
